import { 
  doc, 
  setDoc, 
  writeBatch,
  Timestamp 
} from 'firebase/firestore';
import { db } from './config';
import { 
  Permission,
  PermissionCategory,
  RoleDefinition,
  RolePermissions,
  DataAccessLevel,
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS
} from '../../types/permission.types';
import { UserRole } from '../../types/user.types';

// Define all permission categories
const permissionCategories: PermissionCategory[] = [
  {
    id: 'development_management',
    name: 'development_management',
    displayName: 'Development Management',
    description: 'Permissions related to property developments',
    icon: 'Building2',
    order: 1,
    isSystem: true
  },
  {
    id: 'user_management',
    name: 'user_management',
    displayName: 'User Management',
    description: 'Permissions for managing users and roles',
    icon: 'Users',
    order: 2,
    isSystem: true
  },
  {
    id: 'sales_reservations',
    name: 'sales_reservations',
    displayName: 'Sales & Reservations',
    description: 'Permissions for sales activities and reservations',
    icon: 'ShoppingCart',
    order: 3,
    isSystem: true
  },
  {
    id: 'financial_reports',
    name: 'financial_reports',
    displayName: 'Financial & Reports',
    description: 'Permissions for financial data and reporting',
    icon: 'DollarSign',
    order: 4,
    isSystem: true
  },
  {
    id: 'crm_contacts',
    name: 'crm_contacts',
    displayName: 'CRM & Contacts',
    description: 'Permissions for contact management',
    icon: 'UserCheck',
    order: 5,
    isSystem: true
  },
  {
    id: 'system_admin',
    name: 'system_admin',
    displayName: 'System Administration',
    description: 'System-level administrative permissions',
    icon: 'Settings',
    order: 6,
    isSystem: true
  },
  {
    id: 'partner_management',
    name: 'partner_management',
    displayName: 'Partner Management',
    description: 'Permissions for partner-specific features',
    icon: 'Briefcase',
    order: 7,
    isSystem: true
  }
];

// Define all permissions
const permissions: Permission[] = [
  // Development Management
  {
    id: 'developments.create',
    categoryId: 'development_management',
    name: 'create_development',
    displayName: 'Create Development',
    description: 'Create new property developments',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'developments.read',
    categoryId: 'development_management',
    name: 'read_development',
    displayName: 'View Developments',
    description: 'View property developments',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'developments.update',
    categoryId: 'development_management',
    name: 'update_development',
    displayName: 'Update Development',
    description: 'Edit existing developments',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.UPDATE,
    isSystemPermission: false
  },
  {
    id: 'developments.delete',
    categoryId: 'development_management',
    name: 'delete_development',
    displayName: 'Delete Development',
    description: 'Delete developments',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.DELETE,
    isSystemPermission: false
  },
  {
    id: 'developments.view_all',
    categoryId: 'development_management',
    name: 'view_all_developments',
    displayName: 'View All Developments',
    description: 'View all developments in the system',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.VIEW_ALL,
    isSystemPermission: false
  },
  {
    id: 'developments.export',
    categoryId: 'development_management',
    name: 'export_developments',
    displayName: 'Export Developments',
    description: 'Export development data',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.EXPORT,
    isSystemPermission: false
  },
  {
    id: 'developments.view_financial',
    categoryId: 'development_management',
    name: 'view_development_financials',
    displayName: 'View Development Financials',
    description: 'View financial information for developments',
    resource: PERMISSION_RESOURCES.DEVELOPMENTS,
    action: PERMISSION_ACTIONS.VIEW_FINANCIAL,
    isSystemPermission: false
  },

  // User Management
  {
    id: 'users.create',
    categoryId: 'user_management',
    name: 'create_user',
    displayName: 'Create User',
    description: 'Create new users',
    resource: PERMISSION_RESOURCES.USERS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'users.read',
    categoryId: 'user_management',
    name: 'read_users',
    displayName: 'View Users',
    description: 'View user information',
    resource: PERMISSION_RESOURCES.USERS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'users.update',
    categoryId: 'user_management',
    name: 'update_user',
    displayName: 'Update User',
    description: 'Edit user details',
    resource: PERMISSION_RESOURCES.USERS,
    action: PERMISSION_ACTIONS.UPDATE,
    isSystemPermission: false
  },
  {
    id: 'users.delete',
    categoryId: 'user_management',
    name: 'delete_user',
    displayName: 'Delete User',
    description: 'Delete users',
    resource: PERMISSION_RESOURCES.USERS,
    action: PERMISSION_ACTIONS.DELETE,
    isSystemPermission: false
  },
  {
    id: 'users.assign',
    categoryId: 'user_management',
    name: 'assign_roles',
    displayName: 'Assign Roles',
    description: 'Assign roles to users',
    resource: PERMISSION_RESOURCES.USERS,
    action: PERMISSION_ACTIONS.ASSIGN,
    isSystemPermission: false
  },
  {
    id: 'roles.manage',
    categoryId: 'user_management',
    name: 'manage_roles',
    displayName: 'Manage Roles',
    description: 'Create and modify user roles',
    resource: PERMISSION_RESOURCES.ROLES,
    action: PERMISSION_ACTIONS.MANAGE,
    isSystemPermission: true
  },

  // Sales & Reservations
  {
    id: 'reservations.create',
    categoryId: 'sales_reservations',
    name: 'create_reservation',
    displayName: 'Create Reservation',
    description: 'Create unit reservations',
    resource: PERMISSION_RESOURCES.RESERVATIONS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'reservations.read',
    categoryId: 'sales_reservations',
    name: 'view_reservations',
    displayName: 'View Reservations',
    description: 'View reservation details',
    resource: PERMISSION_RESOURCES.RESERVATIONS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'reservations.update',
    categoryId: 'sales_reservations',
    name: 'update_reservation',
    displayName: 'Update Reservation',
    description: 'Edit reservation details',
    resource: PERMISSION_RESOURCES.RESERVATIONS,
    action: PERMISSION_ACTIONS.UPDATE,
    isSystemPermission: false
  },
  {
    id: 'reservations.extend',
    categoryId: 'sales_reservations',
    name: 'extend_reservation',
    displayName: 'Extend Reservation',
    description: 'Extend reservation periods',
    resource: PERMISSION_RESOURCES.RESERVATIONS,
    action: PERMISSION_ACTIONS.EXTEND,
    isSystemPermission: false
  },
  {
    id: 'reservations.cancel',
    categoryId: 'sales_reservations',
    name: 'cancel_reservation',
    displayName: 'Cancel Reservation',
    description: 'Cancel reservations',
    resource: PERMISSION_RESOURCES.RESERVATIONS,
    action: PERMISSION_ACTIONS.CANCEL,
    isSystemPermission: false
  },
  {
    id: 'deals.create',
    categoryId: 'sales_reservations',
    name: 'create_deal',
    displayName: 'Create Deal',
    description: 'Create sales deals',
    resource: PERMISSION_RESOURCES.DEALS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'deals.update',
    categoryId: 'sales_reservations',
    name: 'update_deal',
    displayName: 'Update Deal',
    description: 'Edit deal details',
    resource: PERMISSION_RESOURCES.DEALS,
    action: PERMISSION_ACTIONS.UPDATE,
    isSystemPermission: false
  },
  {
    id: 'deals.cancel',
    categoryId: 'sales_reservations',
    name: 'cancel_deal',
    displayName: 'Cancel Deal',
    description: 'Cancel deals',
    resource: PERMISSION_RESOURCES.DEALS,
    action: PERMISSION_ACTIONS.CANCEL,
    isSystemPermission: false
  },
  {
    id: 'viewings.create',
    categoryId: 'sales_reservations',
    name: 'book_viewing',
    displayName: 'Book Viewing',
    description: 'Schedule property viewings',
    resource: PERMISSION_RESOURCES.VIEWINGS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },

  // Financial & Reports
  {
    id: 'reports.view',
    categoryId: 'financial_reports',
    name: 'view_reports',
    displayName: 'View Reports',
    description: 'View system reports',
    resource: PERMISSION_RESOURCES.REPORTS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'reports.export',
    categoryId: 'financial_reports',
    name: 'export_reports',
    displayName: 'Export Reports',
    description: 'Export reports to files',
    resource: PERMISSION_RESOURCES.REPORTS,
    action: PERMISSION_ACTIONS.EXPORT,
    isSystemPermission: false
  },
  {
    id: 'reports.view_financial',
    categoryId: 'financial_reports',
    name: 'view_financial_reports',
    displayName: 'View Financial Reports',
    description: 'View financial and commission reports',
    resource: PERMISSION_RESOURCES.REPORTS,
    action: PERMISSION_ACTIONS.VIEW_FINANCIAL,
    isSystemPermission: false
  },
  {
    id: 'invoices.create',
    categoryId: 'financial_reports',
    name: 'create_invoice',
    displayName: 'Create Invoice',
    description: 'Generate invoices',
    resource: PERMISSION_RESOURCES.INVOICES,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'invoices.approve',
    categoryId: 'financial_reports',
    name: 'approve_invoice',
    displayName: 'Approve Invoice',
    description: 'Approve invoices for payment',
    resource: PERMISSION_RESOURCES.INVOICES,
    action: PERMISSION_ACTIONS.APPROVE,
    isSystemPermission: false
  },
  {
    id: 'commissions.view',
    categoryId: 'financial_reports',
    name: 'view_commissions',
    displayName: 'View Commissions',
    description: 'View commission details',
    resource: PERMISSION_RESOURCES.COMMISSIONS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'commissions.manage',
    categoryId: 'financial_reports',
    name: 'manage_commissions',
    displayName: 'Manage Commissions',
    description: 'Configure commission rates',
    resource: PERMISSION_RESOURCES.COMMISSIONS,
    action: PERMISSION_ACTIONS.MANAGE,
    isSystemPermission: false
  },

  // CRM & Contacts
  {
    id: 'contacts.create',
    categoryId: 'crm_contacts',
    name: 'create_contact',
    displayName: 'Create Contact',
    description: 'Add new contacts',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'contacts.read',
    categoryId: 'crm_contacts',
    name: 'view_contacts',
    displayName: 'View Contacts',
    description: 'View contact information',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.READ,
    isSystemPermission: false
  },
  {
    id: 'contacts.update',
    categoryId: 'crm_contacts',
    name: 'update_contact',
    displayName: 'Update Contact',
    description: 'Edit contact details',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.UPDATE,
    isSystemPermission: false
  },
  {
    id: 'contacts.delete',
    categoryId: 'crm_contacts',
    name: 'delete_contact',
    displayName: 'Delete Contact',
    description: 'Remove contacts',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.DELETE,
    isSystemPermission: false
  },
  {
    id: 'contacts.assign',
    categoryId: 'crm_contacts',
    name: 'assign_contact',
    displayName: 'Assign Contact',
    description: 'Assign contacts to agents',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.ASSIGN,
    isSystemPermission: false
  },
  {
    id: 'contacts.export',
    categoryId: 'crm_contacts',
    name: 'export_contacts',
    displayName: 'Export Contacts',
    description: 'Export contact data',
    resource: PERMISSION_RESOURCES.CONTACTS,
    action: PERMISSION_ACTIONS.EXPORT,
    isSystemPermission: false
  },

  // System Administration
  {
    id: 'settings.manage',
    categoryId: 'system_admin',
    name: 'manage_settings',
    displayName: 'Manage Settings',
    description: 'Configure system settings',
    resource: PERMISSION_RESOURCES.SETTINGS,
    action: PERMISSION_ACTIONS.MANAGE,
    isSystemPermission: true
  },
  {
    id: 'activities.view_all',
    categoryId: 'system_admin',
    name: 'view_all_activities',
    displayName: 'View All Activities',
    description: 'View all system activity logs',
    resource: PERMISSION_RESOURCES.ACTIVITIES,
    action: PERMISSION_ACTIONS.VIEW_ALL,
    isSystemPermission: true
  },
  {
    id: 'documents.manage',
    categoryId: 'system_admin',
    name: 'manage_documents',
    displayName: 'Manage Documents',
    description: 'Manage system documents',
    resource: PERMISSION_RESOURCES.DOCUMENTS,
    action: PERMISSION_ACTIONS.MANAGE,
    isSystemPermission: false
  },

  // Partner Management
  {
    id: 'partners.create',
    categoryId: 'partner_management',
    name: 'create_partner',
    displayName: 'Create Partner',
    description: 'Create new partners',
    resource: PERMISSION_RESOURCES.PARTNERS,
    action: PERMISSION_ACTIONS.CREATE,
    isSystemPermission: false
  },
  {
    id: 'partners.manage',
    categoryId: 'partner_management',
    name: 'manage_partners',
    displayName: 'Manage Partners',
    description: 'Manage partner settings',
    resource: PERMISSION_RESOURCES.PARTNERS,
    action: PERMISSION_ACTIONS.MANAGE,
    isSystemPermission: false
  },
  {
    id: 'partners.view_all',
    categoryId: 'partner_management',
    name: 'view_all_partners',
    displayName: 'View All Partners',
    description: 'View all partner information',
    resource: PERMISSION_RESOURCES.PARTNERS,
    action: PERMISSION_ACTIONS.VIEW_ALL,
    isSystemPermission: false
  }
];

// Define default role permissions
const rolePermissionMappings: Record<string, string[]> = {
  // PropCloudAI System Level - Full access to everything
  [UserRole.PROPCLOUDAI_SUPER_USER]: permissions.map(p => p.id), // Super user gets all permissions
  
  [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: permissions.map(p => p.id), // Full user management across all tenants
  
  [UserRole.PROPCLOUDAI_CRM]: permissions.map(p => p.id), // Full CRM access across all tenants
  
  [UserRole.PROPCLOUDAI_ADVERTISING]: permissions.map(p => p.id), // Full advertising access across all tenants
  
  // PropInvest HQ Level
  [UserRole.HQ_MANAGER]: [
    // Development Management
    'developments.create',
    'developments.read',
    'developments.update',
    'developments.delete',
    'developments.view_all',
    'developments.export',
    'developments.view_financial',
    // User Management
    'users.create',
    'users.read',
    'users.update',
    'users.delete',
    'users.assign',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'reservations.extend',
    'reservations.cancel',
    'deals.create',
    'deals.update',
    'deals.cancel',
    'viewings.create',
    // Financial & Reports
    'reports.view',
    'reports.export',
    'reports.view_financial',
    'invoices.create',
    'invoices.approve',
    'commissions.view',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update',
    'contacts.delete',
    'contacts.assign',
    'contacts.export',
    // System Administration
    'activities.view_all',
    'documents.manage',
    // Partner Management
    'partners.manage',
    'partners.view_all'
  ],
  
  [UserRole.HQ_ADMIN]: [
    // Development Management
    'developments.create',
    'developments.read',
    'developments.update',
    'developments.view_all',
    'developments.export',
    // User Management
    'users.read',
    'users.update',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'reservations.extend',
    'deals.create',
    'deals.update',
    'viewings.create',
    // Financial & Reports
    'reports.view',
    'reports.export',
    'invoices.create',
    'commissions.view',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update',
    'contacts.assign',
    // System Administration
    'documents.manage'
  ],
  
  [UserRole.HQ_BONDS]: [
    // Development Management
    'developments.read',
    'developments.view_financial',
    // Sales & Reservations
    'reservations.read',
    'reservations.update',
    'deals.read',
    'deals.update',
    // Financial & Reports
    'reports.view',
    'reports.view_financial',
    'invoices.create',
    'commissions.view',
    // CRM & Contacts
    'contacts.read'
  ],
  
  [UserRole.HQ_CONSULTANT_LVL1]: [ // 30% commission
    // Development Management
    'developments.read',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'deals.create',
    'deals.update',
    'viewings.create',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update'
  ],
  
  [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: [ // 50% commission
    // Development Management
    'developments.read',
    'developments.view_financial',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'reservations.extend',
    'deals.create',
    'deals.update',
    'viewings.create',
    // Financial & Reports
    'reports.view',
    'commissions.view',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update',
    'contacts.assign'
  ],
  
  [UserRole.HQ_REFERRAL_MANDATE]: [ // 5% commission
    // Limited view access
    'developments.read',
    'reservations.read',
    'contacts.read',
    'reports.view'
  ],
  
  [UserRole.INDEPENDENT_PARTNER_LVL3]: [ // 70% commission - can manage agents
    // Development Management
    'developments.read',
    'developments.view_all',
    'developments.view_financial',
    // User Management (for their agents)
    'users.create',
    'users.read',
    'users.update',
    'users.assign',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'reservations.extend',
    'reservations.cancel',
    'deals.create',
    'deals.update',
    'deals.cancel',
    'viewings.create',
    // Financial & Reports
    'reports.view',
    'reports.export',
    'reports.view_financial',
    'invoices.create',
    'commissions.view',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update',
    'contacts.delete',
    'contacts.assign',
    'contacts.export',
    // Partner Management
    'partners.manage'
  ],
  
  [UserRole.PARTNER_AGENT_LVL3]: [ // Works under partner
    // Development Management
    'developments.read',
    // Sales & Reservations
    'reservations.create',
    'reservations.read',
    'reservations.update',
    'deals.create',
    'viewings.create',
    // CRM & Contacts
    'contacts.create',
    'contacts.read',
    'contacts.update'
  ]
};

// Define default roles
const roles: RoleDefinition[] = [
  // PropCloudAI System Level Roles
  {
    id: UserRole.PROPCLOUDAI_SUPER_USER,
    name: 'propcloudai_super_user',
    displayName: 'PropCloudAI Super User',
    description: 'Full system access with all permissions',
    isSystemRole: true,
    isCustom: false,
    order: 1,
    color: '#6eb041',
    icon: 'Shield'
  },
  {
    id: UserRole.PROPCLOUDAI_USER_MANAGEMENT,
    name: 'propcloudai_user_management',
    displayName: 'PropCloudAI User Management',
    description: 'Manage users across all tenants',
    isSystemRole: true,
    isCustom: false,
    order: 2,
    color: '#3b82f6',
    icon: 'UserCog'
  },
  {
    id: UserRole.PROPCLOUDAI_CRM,
    name: 'propcloudai_crm',
    displayName: 'PropCloudAI CRM',
    description: 'Manage CRM across all tenants',
    isSystemRole: true,
    isCustom: false,
    order: 3,
    color: '#8b5cf6',
    icon: 'Users'
  },
  {
    id: UserRole.PROPCLOUDAI_ADVERTISING,
    name: 'propcloudai_advertising',
    displayName: 'PropCloudAI Advertising',
    description: 'Manage advertising across all tenants',
    isSystemRole: true,
    isCustom: false,
    order: 4,
    color: '#ec4899',
    icon: 'Megaphone'
  },
  // PropInvest HQ Level Roles
  {
    id: UserRole.HQ_MANAGER,
    name: 'hq_manager',
    displayName: 'HQ Manager',
    description: 'Manage HQ operations and users',
    isSystemRole: true,
    isCustom: false,
    order: 5,
    color: '#14b8a6',
    icon: 'Settings'
  },
  {
    id: UserRole.HQ_ADMIN,
    name: 'hq_admin',
    displayName: 'HQ Admin',
    description: 'HQ administrative access',
    isSystemRole: true,
    isCustom: false,
    order: 6,
    color: '#0891b2',
    icon: 'Briefcase'
  },
  {
    id: UserRole.HQ_BONDS,
    name: 'hq_bonds',
    displayName: 'HQ Bonds',
    description: 'HQ bond origination and finance',
    isSystemRole: true,
    isCustom: false,
    order: 7,
    color: '#10b981',
    icon: 'Banknote'
  },
  {
    id: UserRole.HQ_CONSULTANT_LVL1,
    name: 'hq_consultant_lvl1',
    displayName: 'HQ Consultant (Level 1)',
    description: 'HQ sales consultant with 30% commission',
    isSystemRole: true,
    isCustom: false,
    order: 8,
    color: '#f59e0b',
    icon: 'Briefcase'
  },
  {
    id: UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    name: 'hq_independent_consultant_lvl2',
    displayName: 'HQ Independent Consultant (Level 2)',
    description: 'HQ independent consultant with 50% commission',
    isSystemRole: true,
    isCustom: false,
    order: 9,
    color: '#ef4444',
    icon: 'UserCheck'
  },
  {
    id: UserRole.HQ_REFERRAL_MANDATE,
    name: 'hq_referral_mandate',
    displayName: 'HQ Referral Mandate',
    description: 'HQ referral with 5% commission',
    isSystemRole: true,
    isCustom: false,
    order: 10,
    color: '#a855f7',
    icon: 'Link'
  },
  {
    id: UserRole.INDEPENDENT_PARTNER_LVL3,
    name: 'independent_partner_lvl3',
    displayName: 'Independent Partner (Level 3)',
    description: 'Independent partner with 70% commission and agent management',
    isSystemRole: true,
    isCustom: false,
    order: 11,
    color: '#dc2626',
    icon: 'Building'
  },
  // Partner Office Level Roles
  {
    id: UserRole.PARTNER_AGENT_LVL3,
    name: 'partner_agent_lvl3',
    displayName: 'Partner Agent (Level 3)',
    description: 'Sales agent working under independent partner',
    isSystemRole: true,
    isCustom: false,
    order: 12,
    color: '#6366f1',
    icon: 'User'
  }
];

export const initializePermissionData = async (): Promise<void> => {
  const batch = writeBatch(db);
  const timestamp = Timestamp.now();

  try {
    console.log('Starting permission system initialization...');
    
    // First, clean up old roles that are not in the new system
    const { getDocs, collection, query } = await import('firebase/firestore');
    console.log('Cleaning up old roles...');
    
    const oldRolesToDelete = [
      'super_user',
      'system_manager', 
      'system_admin',
      'bonds',
      'consultant_30',
      'consultant_50',
      'partner'
    ];
    
    const rolesSnapshot = await getDocs(query(collection(db, 'roles')));
    rolesSnapshot.forEach((roleDoc) => {
      const roleData = roleDoc.data();
      // Delete if it's an old role or not in our new roles list
      if (oldRolesToDelete.includes(roleDoc.id) || 
          oldRolesToDelete.includes(roleData.name) ||
          !roles.find(r => r.id === roleDoc.id)) {
        batch.delete(roleDoc.ref);
        console.log(`Deleting old role: ${roleDoc.id}`);
      }
    });

    // 1. Create permission categories
    console.log('Creating permission categories...');
    for (const category of permissionCategories) {
      batch.set(doc(db, 'permissionCategories', category.id), category);
    }

    // 2. Create permissions
    console.log('Creating permissions...');
    for (const permission of permissions) {
      batch.set(doc(db, 'permissions', permission.id), {
        ...permission,
        createdAt: timestamp,
        updatedAt: timestamp
      });
    }

    // 3. Create roles
    console.log('Creating roles...');
    for (const role of roles) {
      batch.set(doc(db, 'roles', role.id), {
        ...role,
        createdAt: timestamp,
        updatedAt: timestamp
      });
    }

    // 4. Create role permissions
    console.log('Creating role permissions...');
    for (const [roleId, permissionIds] of Object.entries(rolePermissionMappings)) {
      const dataAccessLevel = roleId === UserRole.PROPCLOUDAI_SUPER_USER ? DataAccessLevel.ALL :
                             roleId === UserRole.HQ_MANAGER ? DataAccessLevel.ALL :
                             roleId === UserRole.INDEPENDENT_PARTNER_LVL3 ? DataAccessLevel.PARTNER :
                             roleId === UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2 ? DataAccessLevel.TEAM :
                             DataAccessLevel.OWN;

      batch.set(doc(db, 'rolePermissions', roleId), {
        roleId,
        permissions: permissionIds,
        menuAccess: [],
        customSettings: {
          dataAccessLevel,
          canExport: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER, UserRole.INDEPENDENT_PARTNER_LVL3].includes(roleId as UserRole),
          canBulkEdit: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER].includes(roleId as UserRole),
          canDeletePermanently: roleId === UserRole.PROPCLOUDAI_SUPER_USER
        },
        createdAt: timestamp,
        updatedAt: timestamp
      });
    }

    // Commit all changes
    await batch.commit();
    console.log('Permission system initialized successfully!');
  } catch (error) {
    console.error('Error initializing permission system:', error);
    throw error;
  }
};